import { useState, Fragment, useEffect } from 'react';
import {
  History,
  Filter,
  ChevronDown,
  ChevronRight,
  Calendar,
  Eye,
  Loader2,
  AlertCircle,
  Trash2,
} from 'lucide-react';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Checkbox } from '@/components/ui/checkbox';
import { ResponsiveModal } from '@/components/ui/responsive-modal';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import {
  useAuditLogs,
  formatAuditAction,
  formatEntityType,
  getAuditDiff,
  generateAuditSummary,
  type AuditLogFilters,
} from '@/hooks/useAuditLogs';
import { useTeam } from '@/hooks/useTeam';

export default function AuditLogs() {
  const { teamMembers } = useTeam();
  const [filters, setFilters] = useState<AuditLogFilters>({
    limit: 50,
    offset: 0,
  });
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [showPurgeDialog, setShowPurgeDialog] = useState(false);
  const [purgeType, setPurgeType] = useState<'selected' | 'all'>('selected');

  const {
    logs,
    total,
    isLoading,
    isFetching,
    actionTypes,
    entityTypes,
    canViewSuperadminLogs,
    canPurge,
    purgeSelected,
    purgeAll,
    isPurging,
  } = useAuditLogs(filters);

  // Automatically show superadmin logs for superadmins
  useEffect(() => {
    if (canViewSuperadminLogs && filters.includeSuperadminActions === undefined) {
      setFilters(prev => ({ ...prev, includeSuperadminActions: true }));
    }
  }, [canViewSuperadminLogs, filters.includeSuperadminActions]);

  const toggleSelectAll = () => {
    if (selectedIds.size === logs.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(logs.map(log => log.id)));
    }
  };

  const toggleSelect = (id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const handlePurge = async () => {
    try {
      let count: number;
      if (purgeType === 'all') {
        count = await purgeAll();
      } else {
        count = await purgeSelected(Array.from(selectedIds));
      }
      toast.success('Audit logs purged', {
        description: `Successfully deleted ${count} audit log${count !== 1 ? 's' : ''}.`,
      });
      setSelectedIds(new Set());
    } catch (error) {
      toast.error('Error', {
        description: 'Failed to purge audit logs. Please try again.',
      });
    } finally {
      setShowPurgeDialog(false);
    }
  };

  const toggleRow = (id: string) => {
    setExpandedRows(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const header = (
    <div className="flex items-center justify-between gap-4">
      <div>
        <h1 className="text-2xl font-display font-semibold tracking-tight">
          Audit Logs
        </h1>
        <p className="text-sm text-muted-foreground">
          Track all actions and changes across your organization
        </p>
      </div>
      <div className="flex items-center gap-2">
        {isFetching && !isLoading && (
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        )}
        {canPurge && (
          <>
            {selectedIds.size > 0 && (
              <Button
                variant="destructive"
                size="sm"
                onClick={() => {
                  setPurgeType('selected');
                  setShowPurgeDialog(true);
                }}
                disabled={isPurging}
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Delete Selected ({selectedIds.size})
              </Button>
            )}
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                setPurgeType('all');
                setShowPurgeDialog(true);
              }}
              disabled={isPurging || logs.length === 0}
              className="text-destructive hover:text-destructive"
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Purge All
            </Button>
          </>
        )}
      </div>
    </div>
  );

  return (
    <DashboardLayout header={header}>
      {/* Filters */}
      <Card className="mb-6">
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Filter className="h-4 w-4" />
            Filters
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="space-y-2">
              <Label>User</Label>
              <Select
                value={filters.userId || 'all'}
                onValueChange={(value) => 
                  setFilters({ ...filters, userId: value === 'all' ? undefined : value, offset: 0 })
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="All users" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Users</SelectItem>
                  {teamMembers.map(member => (
                    <SelectItem key={member.id} value={member.id}>
                      {member.first_name} {member.last_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Action</Label>
              <Select
                value={filters.action || 'all'}
                onValueChange={(value) => 
                  setFilters({ ...filters, action: value === 'all' ? undefined : value, offset: 0 })
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="All actions" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Actions</SelectItem>
                  {actionTypes.map(action => (
                    <SelectItem key={action} value={action}>
                      {formatAuditAction(action)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Entity Type</Label>
              <Select
                value={filters.entityType || 'all'}
                onValueChange={(value) => 
                  setFilters({ ...filters, entityType: value === 'all' ? undefined : value, offset: 0 })
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="All types" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  {entityTypes.map(type => (
                    <SelectItem key={type} value={type}>
                      {formatEntityType(type)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Date Range</Label>
              <div className="flex gap-2">
                <Input
                  type="date"
                  value={filters.startDate || ''}
                  onChange={(e) => 
                    setFilters({ ...filters, startDate: e.target.value || undefined, offset: 0 })
                  }
                  className="flex-1"
                />
                <Input
                  type="date"
                  value={filters.endDate || ''}
                  onChange={(e) => 
                    setFilters({ ...filters, endDate: e.target.value || undefined, offset: 0 })
                  }
                  className="flex-1"
                />
              </div>
            </div>
          </div>

          {canViewSuperadminLogs && (
            <div className="flex items-center gap-2 mt-4 pt-4 border-t">
              <Switch
                id="superadmin-logs"
                checked={filters.includeSuperadminActions || false}
                onCheckedChange={(checked) => 
                  setFilters({ ...filters, includeSuperadminActions: checked, offset: 0 })
                }
              />
              <Label htmlFor="superadmin-logs" className="text-sm">
                Include superadmin actions
              </Label>
              <Badge variant="outline" className="ml-2 text-xs">
                Superadmin only
              </Badge>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Logs Table */}
      {isLoading ? (
        <div className="flex items-center justify-center py-16">
          <div className="flex items-center gap-2 text-muted-foreground">
            <Loader2 className="h-5 w-5 animate-spin" />
            <span>Loading audit logs...</span>
          </div>
        </div>
      ) : logs.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center mb-4">
            <History className="h-8 w-8 text-muted-foreground" />
          </div>
          <h3 className="font-medium">No audit logs found</h3>
          <p className="text-sm text-muted-foreground mt-1">
            Try adjusting your filters
          </p>
        </div>
      ) : (
        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b bg-muted/30">
                    {canPurge && (
                      <th className="w-10 p-3">
                        <Checkbox
                          checked={selectedIds.size === logs.length && logs.length > 0}
                          onCheckedChange={toggleSelectAll}
                          aria-label="Select all"
                        />
                      </th>
                    )}
                    <th className="w-8 p-3"></th>
                    <th className="text-left p-3 font-medium text-muted-foreground text-sm">Timestamp</th>
                    <th className="text-left p-3 font-medium text-muted-foreground text-sm">User</th>
                    <th className="text-left p-3 font-medium text-muted-foreground text-sm">Action</th>
                    <th className="text-left p-3 font-medium text-muted-foreground text-sm">Entity</th>
                    <th className="text-left p-3 font-medium text-muted-foreground text-sm">Details</th>
                  </tr>
                </thead>
                <tbody>
                  {logs.map(log => {
                    const isExpanded = expandedRows.has(log.id);
                    const diff = getAuditDiff(log.old_values, log.new_values);
                    const hasDetails = diff.length > 0 || log.performed_as;

                    return (
                      <Fragment key={log.id}>
                        <tr
                          className={cn(
                            'border-b hover:bg-muted/50 cursor-pointer transition-colors',
                            isExpanded && 'bg-muted/30',
                            log.is_superadmin_action && 'bg-purple-50/50 dark:bg-purple-950/20',
                            selectedIds.has(log.id) && 'bg-primary/5'
                          )}
                          onClick={() => hasDetails && toggleRow(log.id)}
                        >
                          {canPurge && (
                            <td className="p-3" onClick={(e) => e.stopPropagation()}>
                              <Checkbox
                                checked={selectedIds.has(log.id)}
                                onCheckedChange={() => toggleSelect(log.id)}
                                aria-label={`Select log ${log.id}`}
                              />
                            </td>
                          )}
                          <td className="p-3">
                            {hasDetails && (
                              isExpanded ? (
                                <ChevronDown className="h-4 w-4 text-muted-foreground" />
                              ) : (
                                <ChevronRight className="h-4 w-4 text-muted-foreground" />
                              )
                            )}
                          </td>
                          <td className="p-3">
                            <div className="flex items-center gap-2 text-sm">
                              <Calendar className="h-3 w-3 text-muted-foreground" />
                              {formatDate(log.created_at)}
                            </div>
                          </td>
                          <td className="p-3">
                            <div className="flex items-center gap-2">
                              <div className="flex h-7 w-7 items-center justify-center rounded-full bg-primary/10 text-xs font-medium text-primary">
                                {log.user?.first_name?.[0] || log.user?.last_name?.[0]
                                  ? `${log.user?.first_name?.[0] || ''}${log.user?.last_name?.[0] || ''}`
                                  : '⚙'}
                              </div>
                              <div>
                                <p className="text-sm font-medium">
                                  {log.user?.first_name || log.user?.last_name
                                    ? `${log.user?.first_name || ''} ${log.user?.last_name || ''}`.trim()
                                    : 'System'}
                                </p>
                                <p className="text-xs text-muted-foreground capitalize">
                                  {log.user?.role || 'Automated'}
                                </p>
                              </div>
                            </div>
                          </td>
                          <td className="p-3">
                            <Badge variant="outline">
                              {formatAuditAction(log.action)}
                            </Badge>
                            {log.is_superadmin_action && (
                              <Badge variant="secondary" className="ml-1 text-xs">
                                SA
                              </Badge>
                            )}
                          </td>
                          <td className="p-3">
                            <span className="text-sm">
                              {formatEntityType(log.entity_type)}
                            </span>
                            {log.entity_id && (
                              <span className="text-xs text-muted-foreground ml-1">
                                ({log.entity_id.slice(0, 8)}...)
                              </span>
                            )}
                          </td>
                          <td className="p-3">
                            <div className="max-w-[300px]">
                              <p className="text-sm text-foreground truncate" title={generateAuditSummary(log)}>
                                {generateAuditSummary(log)}
                              </p>
                              {log.performed_as && (
                                <div className="flex items-center gap-1 text-xs text-amber-600 mt-1">
                                  <Eye className="h-3 w-3" />
                                  Impersonating {log.performed_as_user?.first_name}
                                </div>
                              )}
                            </div>
                          </td>
                        </tr>
                        {isExpanded && hasDetails && (
                          <tr key={`${log.id}-details`} className="bg-muted/20">
                            <td colSpan={canPurge ? 7 : 6} className="p-4">
                              <div className="space-y-4">
                                {log.performed_as && (
                                  <div className="flex items-center gap-2 p-3 rounded-lg bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-900">
                                    <AlertCircle className="h-4 w-4 text-amber-600" />
                                    <span className="text-sm text-amber-700 dark:text-amber-400">
                                      Action performed while impersonating{' '}
                                      <strong>
                                        {log.performed_as_user?.first_name} {log.performed_as_user?.last_name}
                                      </strong>
                                    </span>
                                  </div>
                                )}

                                {diff.length > 0 && (
                                  <div>
                                    <h4 className="text-sm font-medium mb-2">Changes</h4>
                                    <div className="border rounded-lg overflow-hidden">
                                      <table className="w-full text-sm">
                                        <thead>
                                          <tr className="bg-muted/50">
                                            <th className="text-left p-2 font-medium">Field</th>
                                            <th className="text-left p-2 font-medium">Old Value</th>
                                            <th className="text-left p-2 font-medium">New Value</th>
                                          </tr>
                                        </thead>
                                        <tbody>
                                          {diff.map((change, idx) => (
                                            <tr key={idx} className="border-t">
                                              <td className="p-2 font-medium">{change.field}</td>
                                              <td className="p-2 text-red-600">
                                                {change.formattedOld}
                                              </td>
                                              <td className="p-2 text-green-600">
                                                {change.formattedNew}
                                              </td>
                                            </tr>
                                          ))}
                                        </tbody>
                                      </table>
                                    </div>
                                  </div>
                                )}

                                {log.ip_address && (
                                  <p className="text-xs text-muted-foreground">
                                    IP Address: {log.ip_address}
                                  </p>
                                )}
                              </div>
                            </td>
                          </tr>
                        )}
                      </Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div className="flex items-center justify-between p-4 border-t">
              <p className="text-sm text-muted-foreground">
                Showing {logs.length} of {total} logs
              </p>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setFilters({ ...filters, offset: Math.max(0, (filters.offset || 0) - 50) })}
                  disabled={(filters.offset || 0) === 0}
                >
                  Previous
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setFilters({ ...filters, offset: (filters.offset || 0) + 50 })}
                  disabled={logs.length < 50}
                >
                  Next
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Purge Confirmation Dialog */}
      <ResponsiveModal open={showPurgeDialog} onOpenChange={setShowPurgeDialog}>
        <div className="space-y-4">
          <div>
            <h2 className="text-lg font-semibold">
              {purgeType === 'all' ? 'Purge All Audit Logs?' : 'Delete Selected Audit Logs?'}
            </h2>
            <p className="text-sm text-muted-foreground mt-2">
              {purgeType === 'all' ? (
                <>
                  This will permanently delete <strong>all {total}</strong> audit log entries.
                  This action cannot be undone and will not be tracked.
                </>
              ) : (
                <>
                  This will permanently delete <strong>{selectedIds.size}</strong> selected audit log
                  {selectedIds.size !== 1 ? ' entries' : ' entry'}.
                  This action cannot be undone and will not be tracked.
                </>
              )}
            </p>
          </div>
          <div className="flex justify-end gap-2">
            <Button
              variant="outline"
              onClick={() => setShowPurgeDialog(false)}
              disabled={isPurging}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handlePurge}
              disabled={isPurging}
            >
              {isPurging ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Deleting...
                </>
              ) : (
                <>
                  <Trash2 className="h-4 w-4 mr-2" />
                  {purgeType === 'all' ? 'Purge All' : 'Delete Selected'}
                </>
              )}
            </Button>
          </div>
        </div>
      </ResponsiveModal>
    </DashboardLayout>
  );
}

